import {
	createRouter,
	createWebHashHistory,
	RouteLocationNormalized,
	RouteRecordRaw,
} from 'vue-router'
import store from '@/store'
import {getConfig} from '@/services/client.service'
import {useAuth} from '@/composables/useAuth'
import {useContextStore} from '@/store/modules/context'
import {isStandalonePWA} from 'is-standalone-pwa'
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';

const DevView = () => import('@/views/DevView.vue')
const BetBuilderView = () => import('@/views/BetBuilderView.vue')
const OddsView = () => import('@/views/OddsView.vue')
const ContextListView = () => import('@/views/ContextListView.vue')
const AuthQRView = () => import('@/views/AuthQrView.vue')
const NoConnectionView = () => import('@/views/NoConnectionView.vue')
const ServerErrorView = () => import('@/views/ServerErrorView.vue')
const ExpiredOrNotFoundShareView = () => import('@/views/ExpiredOrNotFoundShareView.vue')

const isPwa = isStandalonePWA()

function setFavicon(iconUrl: string) {
	console.log("**** SETTING " + iconUrl)
	let link: HTMLLinkElement | null = document.querySelector("link[rel*='icon']")
	if (!link) {
		link = document.createElement('link')
		link.rel = 'icon'
		document.head.appendChild(link)
	}
	link.href = iconUrl
}

export type OperationMode = 'direct' | 'odds' | 'dev' | 'betpic'

const splashMode = import.meta.env.VITE_SPLASH_MODE ?? ''

const routes: RouteRecordRaw[] = [
	{path: '/', name: 'index', component: DevView, meta: { hideNavbar: true }},
	{
		path: '/qrLogin/:token',
		name: 'qrLogin',
		component: AuthQRView,
		props: true,
		meta: { hideNavbar: true },
	},
	{path: '/odds', name: 'odds', component: ContextListView},
	{
		path: '/odds/:context',
		name: 'oddsBuilder',
		component: OddsView,
		props: (route: RouteLocationNormalized) => {
			const context = route.params.context as string
			const queryScopes = route.query.scopes
			if (!queryScopes) return {context}
			if (!Array.isArray(queryScopes))
				return {context, scopes: (queryScopes as string).split(',')}
			return {context, scopes: queryScopes}
		},
	},
	{path: '/bet', name: 'bet', component: ContextListView},
	{path: '/bet/no-connection', name: 'noConnectionBet',	component: NoConnectionView, meta: { hideNavbar: true }},
	{path: '/bet/expired-or-not-found', name: 'expiredOrNotFoundBet', component: ExpiredOrNotFoundShareView, meta: { hideNavbar: true }},
	{path: '/bet/server-error', name: 'serverErrorBet', component: ServerErrorView, meta: { hideNavbar: true }},
	{
		path: '/bet/:context',
		name: 'betBuilder',
		component: BetBuilderView,
		props: (route: RouteLocationNormalized) => {
			const context = route.params.context as string
			const queryScopes = route.query.scopes
			if (!queryScopes) return {context}
			if (!Array.isArray(queryScopes))
				return {context, scopes: (queryScopes as string).split(',')}
			return {context, scopes: queryScopes}
		},
	},
	{path: '/betpic', name: 'betpic', component: ContextListView},
	{path: '/betpic/no-connection', name: 'noConnectionBetpic',	component: NoConnectionView, meta: { hideNavbar: true }},
	{path: '/betpic/expired-or-not-found', name: 'expiredOrNotFoundBetPic', component: ExpiredOrNotFoundShareView, meta: { hideNavbar: true }},
	{path: '/betpic/server-error', name: 'serverErrorBetPic', component: ServerErrorView, meta: { hideNavbar: true }},
	{
		path: '/betpic/:context',
		name: 'betpicBuilder',
		component: BetBuilderView,
		props: (route: RouteLocationNormalized) => {
			const context = route.params.context as string
			const queryScopes = route.query.scopes
			if (!queryScopes) return {context}
			if (!Array.isArray(queryScopes))
				return {context, scopes: (queryScopes as string).split(',')}
			return {context, scopes: queryScopes}
		},
	},
	{path: '/dev', name: 'dev', component: DevView, meta: { hideNavbar: true }},
	{path: '/no-connection', name: 'noConnection', component: NoConnectionView, meta: { hideNavbar: true }},
		
	{path: '/odds/no-connection',	name: 'noConnectionOdds',	component: NoConnectionView, meta: { hideNavbar: true }},
	{path: '/odds/expired-or-not-found', name: 'expiredOrNotFoundOdds', component: ExpiredOrNotFoundShareView, meta: { hideNavbar: true }},
	{path: '/odds/server-error', name: 'serverErrorOdds', component: ServerErrorView, meta: { hideNavbar: true }},
		
	{path: '/server-error', name: 'serverError', component: ServerErrorView, meta: { hideNavbar: true }},
	{path: '/expired-or-not-found', name: 'expiredOrNotFound', component: ExpiredOrNotFoundShareView, meta: { hideNavbar: true }},
	{path: '/:catchAll(.*)', name: 'notFound', redirect: '/'},
]

const router = createRouter({
	history: createWebHashHistory(import.meta.env.BASE_URL),
	routes,
})

router.beforeEach(async (to, from, next) => {
	const auth = useAuth()
	await auth.checkLogin()

	if (Capacitor.isNativePlatform()) {
		await StatusBar.setOverlaysWebView({ overlay: false });
		await StatusBar.setStyle({ style: Style.Dark });
	}

	if (to.path.includes('/no-connection')) return next()

	if (to.path.includes('/expired-or-not-found')) return next()

	if (store.getters['configModule/operationMode'] === '') {
		try {
			const configResponse = await getConfig()
			store.dispatch('configModule/setOperationMode', configResponse.operationMode)
		} catch (error: any) {
			console.error('Failed to get initial config:', error)
			// Don't redirect if it's a 401 (auth issue)
			if (error.response?.status !== 401) {
				const mode = to.path.includes('betpic') ? 'betpic' : to.path.includes('odds') ? 'odds' : store.getters['configModule/operationMode'] || import.meta.env.VITE_SPLASH_MODE
				const prefix = mode === 'betpic' ? '/betpic' : mode === 'odds' ? '/odds' : '/bet'
				return next(`${prefix}/no-connection`)
			}
		}
	}

	const operationMode: OperationMode =
			store.getters['configModule/operationMode']

	// In dev mode on a native platform, use VITE_SPLASH_MODE to act as 'direct' or 'odds'
	let effectiveMode: OperationMode = operationMode
	if (operationMode === 'dev' && Capacitor.isNativePlatform()) {
		if (splashMode === 'direct' || splashMode === 'odds' || splashMode === 'betpic') {
			effectiveMode = splashMode as OperationMode
		}
	}

	const queryParam = to.query.query
	if (typeof queryParam === 'string') {
		await store.dispatch('betBuilderModule/setQuery', queryParam)
	}

	if (to.path === '/' || to.path === '') {
		if (effectiveMode === 'odds') {
			return next('/odds')
		} else if (effectiveMode === 'direct') {
			return next('/bet')
		} else if (effectiveMode === 'betpic') {
			return next('/betpic')
		}
	}

	if (to.path === '/odds' && !queryParam) {
		const selectedSport = localStorage.getItem('selectedSport')
		if (selectedSport === 'SOCCER') return next('/odds/supersetSoccer')
		if (selectedSport === 'AMERICAN_FOOTBALL')
			return next('/odds/supersetAmericanFootball')
		if (selectedSport === 'HORSE_RACING')
			return next('/odds/supersetRacing')
	}

	const modeAllowedPaths: Record<OperationMode, string[]> = {
		direct: ['/', '/bet', '/bet/:context', '/qrLogin/:token'],
		odds: ['/', '/odds', '/odds/:context', '/qrLogin/:token'],
		betpic: ['/', '/betpic', '/betpic/:context', '/qrLogin/:token'],
		dev: [
			'/',
			'/dev',
			'/bet',
			'/bet/:context',
			'/betpic',
			'/betpic/:context',
			'/odds',
			'/odds/:context',
			'/qrLogin/:token',
			'/no-connection',
			'/expired-or-not-found',
		],
	}

	const allowedPaths = modeAllowedPaths[effectiveMode]
	const isAllowed = allowedPaths.some((pattern) => {
		const regex = new RegExp(
				'^' + pattern.replace(/:[^\s/]+/g, '[^/]+') + '$'
		)
		return regex.test(to.path)
	}) || to.path.includes('/no-connection') || to.path.includes('/expired-or-not-found')

	if (isAllowed) {
		next()
	} else if (!queryParam) {
		if (effectiveMode === 'direct') next('/bet')
		else if (effectiveMode === 'odds') next('/odds')
		else if (effectiveMode === 'betpic') next('/betpic')
		else next('/')
	} else {
		next()
	}
})

router.afterEach((to) => {
	const contextStore = useContextStore()

	const isContextRoute = to.matched.some((r) => r.path.includes(':context'))

	let ctx = (to.params?.context as string) || ''

	if (!ctx && isContextRoute) {
		try {
			const storeRef =
					(require as any)('@/store').default ||
					(require as any)('@/store/index').default

			ctx = storeRef?.getters?.context || ''
		} catch (_) {
		}
	}

	const contextInfo = ctx ? contextStore.contextMap[ctx] : null
	const brandValue = contextInfo?.brand

	const appClientHome: OperationMode =
			store.getters['configModule/appClientHome']

	const brandName =
			(brandValue &&
					store.getters['configModule/themes'][brandValue]?.name) ||
			''

	const label = appClientHome ? null : brandName

	let baseTitle = 'Voxbet - DEV Mode'
	let favicon = 'favicon-voxbet.ico'

	if (isPwa) {
		document.title = ''
	} else {
		if (to.path.startsWith('/odds')) {
			baseTitle = 'Ask Odds'
			favicon = 'favicon-askodds.svg'
			document.title = baseTitle
		} else if (to.path.startsWith('/betpic')) {
			baseTitle = 'BetPic'
			favicon = 'favicon-betpic.svg'
			document.title = baseTitle
		} else if (to.path.startsWith('/bet')) {
			baseTitle = 'Ask Bookmaker'
			favicon = 'favicon-askbookmaker.svg'
			document.title = label || baseTitle
		}
	}

	setFavicon(import.meta.env.BASE_URL + favicon)
})

export default router